import { Location } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { AdCategory } from '../../models/ad-categories.model';
import { MapCoordinates } from '../../models/map-parameters.model';
import { User } from '../../models/user.model';
import { ClosestCityResolverService } from '../../services/closest-city-resolver.service';
import { EmailVerifyModalService } from '../../services/email-verify-modal.service';
import { Plans } from '../../shared/constants/plan.constants';
import { Roles } from '../../shared/constants/role.constants';
import { Statuses } from '../../shared/constants/status.constants';
import { deleteMyAccount } from '../../store/actions/auth.actions';
import { getUser, updateUser } from '../../store/actions/users.action';
import { selectAuthUser } from '../../store/selectors/auth.selectors';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import {
  selectUser,
  selectUserIsSaving,
  selectUserSavingError,
} from './../../store/selectors/users.selectors';

@Component({
  selector: 'app-user-details',
  standalone: false,
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  loading = false;
  isMyPage = false;

  userId: number | undefined;
  user$ = this.store.select(selectUser);
  authUser$ = this.store.select(selectAuthUser);

  form = this.fb.group({
    id: [null],
    name: this.fb.control<string>('', {
      nonNullable: false,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(25)],
    }),
    email: [{ value: null, disabled: true }],
    status: [null],
    phoneNumber: [null, [Validators.required]],
    role: [null],
    officePhoneNumber: [null],
    description: [null],
    officePhotoUrl: [null],
    plan: [null],
    planUpgradeMonths: [null],
    city: [''],
    addressText: [null],
    latitude: [0],
    longitude: [0],
  });

  isSubmitted = false;
  isEditing = false;
  isAdminEdit = false;
  isOtherEditorEdit = false;
  selectedFile?: File;
  selectedFilePreview?: string;
  photoUrl?: string;

  showDeleteAccountConfirmation = false;

  roles = Roles;
  plans = Plans;
  statuses = Statuses;
  readonly statusOptions = [
    { code: Statuses.Approved, description: Statuses.Approved },
    { code: Statuses.Pending, description: Statuses.Pending },
    { code: Statuses.Blocked, description: Statuses.Blocked },
  ] as AdCategory[];
  planOptions = [
    { code: Plans.Flex, description: Plans.Flex } as AdCategory,
    { code: Plans.Pro, description: Plans.Pro } as AdCategory,
    { code: Plans.Unlimited, description: Plans.Unlimited } as AdCategory,
  ];
  planExtenstionOptions = [
    { code: 1, value: '1_MONTH' },
    { code: 3, value: '3_MONTH' },
    { code: 6, value: '6_MONTH' },
    { code: 12, value: '12_MONTH' },
  ];

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);

  private destroy$ = new Subject<void>();

  private cityResolver = inject(ClosestCityResolverService);

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private fb: FormBuilder,
    private store: Store,
    private toastr: ToastrService,
    private modalService: EmailVerifyModalService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.route.paramMap
      .pipe(
        takeUntil(this.destroy$),
        map((params) => Number(params.get('id'))),
        tap((id) => {
          this.userId = id;
          this.loading = true;
          this.store.dispatch(getUser({ userId: id }));
        })
      )
      .subscribe();

    combineLatest([this.user$, this.authUser$])
      .pipe(
        takeUntil(this.destroy$),
        tap(([user, authUser]) => {
          if (user && this.userId == user.id) {
            this.initForm(user);
            this.isMyPage = !!authUser && authUser.id === user.id;
            this.loading = false;

            this.isAdminEdit = false;

            if (user && authUser) {
              this.isAdminEdit = user.role === Roles.Admin && authUser.role === Roles.Editor;
              this.isOtherEditorEdit =
                user.role === Roles.Editor &&
                authUser.role === Roles.Editor &&
                user.id !== authUser.id;
            }
          }
        })
      )
      .subscribe();

    this.plan?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((newPlane) => {
      if (newPlane === Plans.Flex || newPlane === Plans.Pro || newPlane === Plans.Unlimited) {
        this.planUpgradeMonths?.enable({ emitEvent: false });
      } else {
        this.planUpgradeMonths?.reset(null, { emitEvent: false });
        this.planUpgradeMonths?.disable();
      }
    });
  }

  async locationPicked(mapCoordinates: MapCoordinates): Promise<void> {
    this.latitude?.setValue(mapCoordinates.latitude);
    this.longitude?.setValue(mapCoordinates.longitude);
    const city = await this.ResolveCity(mapCoordinates.latitude, mapCoordinates.longitude);
    this.city?.setValue(city);

    this.form.markAsDirty();
  }

  async ResolveCity(lat: number, lng: number) {
    return await this.cityResolver.getNearestCity(lat, lng);
  }

  goBack() {
    if (this.isEditing) {
      this.isEditing = false;
    } else {
      this.location.back();
    }
  }

  initForm(user: User | undefined): void {
    this.form.patchValue(user as any);
    this.photoUrl = user?.officePhotoUrl;
  }

  onFileSelected(event: any) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    this.selectedFile = event.target.files[0];

    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.selectedFilePreview = e.target.result;
    };
    reader.readAsDataURL(event.target.files[0]);

    this.form.markAsDirty();
  }

  removeExistingPicture() {
    this.photoUrl = undefined;
    this.officePhotoUrl?.setValue(null);
    this.form.markAsDirty();
  }

  removeImage(): void {
    this.selectedFile = undefined;
  }

  toggleEdit(user: User) {
    this.isEditing = !this.isEditing;
    if (this.isEditing) {
      this.initForm(user);
    }
  }

  cancel() {
    this.isEditing = false;
  }

  save() {
    this.isSubmitted = true;
    this.loading = true;

    if (this.form.invalid) {
      this.toastr.error(
        this.translate.instant('VALIDATION_FIX_ERRORS'),
        this.translate.instant('VALIDATION_ERROR')
      );
      return;
    }

    if (this.isAdminEdit) {
      this.toastr.error(this.translate.instant('ADMIN_EDIT_FORBIDDEN'));
      return;
    }
    if (this.isOtherEditorEdit) {
      this.toastr.error(this.translate.instant('EDITOR_EDIT_FORBIDDEN'));
      return;
    }

    this.store.dispatch(
      updateUser({
        user: this.form.getRawValue() as unknown as User,
        selectedFile: this.selectedFile,
      })
    );
    combineLatest([this.store.select(selectUserIsSaving), this.store.select(selectUserSavingError)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isSaving, error]) => {
        if (!isSaving && !error) {
          this.loading = false;
          this.isEditing = false;
        } else if (!isSaving && error) {
          this.loading = false;
        }
      });
  }

  openModal(user: User) {
    this.modalService.open(user?.email ?? '');
  }

  confirmDeleteAccount(): void {
    this.showDeleteAccountConfirmation = true;
  }

  cancelDeleteAccount(): void {
    this.showDeleteAccountConfirmation = false;
  }

  confirmAccountDeletion(): void {
    this.showDeleteAccountConfirmation = false;
    this.deleteAccount();
  }

  async deleteAccount(): Promise<void> {
    this.store.dispatch(deleteMyAccount());
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControls() {
    return this.form?.controls;
  }
  get name() {
    return this.form?.get('name');
  }
  get phoneNumber() {
    return this.form?.get('phoneNumber');
  }
  get officePhoneNumber() {
    return this.form?.get('officePhoneNumber');
  }
  get officePhotoUrl() {
    return this.form?.get('officePhotoUrl');
  }
  get plan() {
    return this.form?.get('plan');
  }
  get planUpgradeMonths() {
    return this.form?.get('planUpgradeMonths');
  }
  get latitude() {
    return this.form.get('latitude');
  }
  get longitude() {
    return this.form.get('longitude');
  }
  get city() {
    return this.form.get('city');
  }
  get addressText() {
    return this.form.get('addressText');
  }
}
